import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChatText, EnvelopeSimple, Eye, EyeSlash, LockKey, User } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import { RegisterRequest } from '../../types';
import AuthShell from './AuthShell';
import { AuthRedirectState, clearAuthRedirectState, getAuthIntent } from '../../hooks/useRequireAuth';

const fieldClass = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/10 dark:border-white/10 dark:bg-white/[0.06] dark:text-white';

const RegisterForm: React.FC = () => {
  const { register: registerUser, isLoading, error, clearError } = useAuthStore();
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterRequest>();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authState = location.state as AuthRedirectState | null;
  const authIntent = getAuthIntent(authState);

  const onSubmit = async (data: RegisterRequest) => {
    try {
      await registerUser(data.email, data.password, data.name, data.bio);
      clearAuthRedirectState();
      navigate('/app', { replace: true });
    } catch {
      // The auth store exposes the error to the form.
    }
  };

  React.useEffect(() => clearError(), [clearError]);

  return (
    <AuthShell eyebrow="Join the map" title="Create your identity" description={authIntent ? `Create an account to ${authIntent}. You’ll return to the map afterwards.` : 'Start simple—you can add a photo or emoji after joining.'}>
      <form className="space-y-4 rounded-[28px] border border-white/80 bg-white/85 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.055] sm:p-6" onSubmit={handleSubmit(onSubmit)}>
        {error && <div role="alert" className="rounded-2xl border border-pink-500/20 bg-pink-500/10 px-4 py-3 text-sm text-pink-700 dark:text-pink-200">{error}</div>}

        <div>
          <label htmlFor="email" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><EnvelopeSimple size={17} className="text-cyan-500" /> Email address</label>
          <input id="email" autoComplete="email" {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Enter a valid email address' } })} type="email" className={`${fieldClass} ${errors.email ? 'border-pink-400' : ''}`} placeholder="you@example.com" />
          {errors.email && <p className="mt-1.5 text-xs text-pink-600 dark:text-pink-300">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><LockKey size={17} className="text-violet-500" /> Password</label>
          <div className="relative">
            <input id="password" autoComplete="new-password" {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Use at least 6 characters' } })} type={showPassword ? 'text' : 'password'} className={`${fieldClass} pr-12 ${errors.password ? 'border-pink-400' : ''}`} placeholder="At least 6 characters" />
            <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)} className="absolute right-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10">{showPassword ? <EyeSlash size={19} /> : <Eye size={19} />}</button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs text-pink-600 dark:text-pink-300">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="name" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><User size={17} className="text-cyan-500" /> Display name <span className="font-normal text-slate-400">optional</span></label>
          <input id="name" autoComplete="name" {...register('name')} type="text" className={fieldClass} placeholder="How people will see you" />
        </div>

        <div>
          <label htmlFor="bio" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><ChatText size={17} className="text-pink-500" /> Short bio <span className="font-normal text-slate-400">optional</span></label>
          <textarea id="bio" {...register('bio')} rows={2} className={`${fieldClass} resize-none`} placeholder="A little about you" />
        </div>

        <button type="submit" disabled={isLoading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-violet-500 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50">
          {isLoading ? 'Creating account…' : 'Create account'} {!isLoading && <ArrowRight size={17} weight="bold" />}
        </button>

        <button type="button" onClick={() => navigate('/login', { state: authState })} className="w-full text-center text-sm text-slate-500 transition hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-300">Already on mapbl? <span className="font-semibold">Sign in</span></button>
      </form>
    </AuthShell>
  );
};

export default RegisterForm;
